import { Storage } from "./Storage";
import { TEMPLATES, MissionType } from "../content/missions";
import { hashString, mulberry32, todayKey, pick, rangeInt } from "../utils/random";

export interface ActiveMission {
  id: string;
  type: MissionType;
  goal: number;
  progress: number;
  reward: number;
  done: boolean;
}

/**
 * Daily mission engine. Uses seeded RNG keyed on the local date so the
 * day's three missions are stable across reloads. Rolls over on the next
 * boot or run-end after midnight in the player's local time zone.
 */
export class Missions {
  constructor(private storage: Storage) {}

  ensureFresh(): ActiveMission[] {
    const day = todayKey();
    const data = this.storage.get();
    if (data.missions.dayKey !== day || data.missions.daily.length === 0) {
      const fresh = this.generate(day);
      this.storage.update((d) => {
        d.missions.dayKey = day;
        d.missions.daily = fresh.map((m) => ({
          id: m.id,
          goal: m.goal,
          progress: m.progress,
          reward: m.reward,
          done: m.done,
        })) as any;
      });
    }
    return this.read();
  }

  read(): ActiveMission[] {
    const data = this.storage.get();
    // Reconstruct full ActiveMission objects, including type lost in storage.
    return data.missions.daily.map((m) => {
      // The id encodes the template index: e.g. "distance:0:1500".
      const [type] = m.id.split(":");
      return {
        id: m.id,
        type: type as MissionType,
        goal: m.goal,
        progress: m.progress,
        reward: m.reward,
        done: m.done,
      };
    });
  }

  private generate(day: string): ActiveMission[] {
    const rng = mulberry32(hashString(day));
    // Pick 3 distinct templates
    const indices: number[] = [];
    while (indices.length < 3 && indices.length < TEMPLATES.length) {
      const i = rangeInt(rng, 0, TEMPLATES.length - 1);
      if (!indices.includes(i)) indices.push(i);
    }
    return indices.map((i, slot) => {
      const t = TEMPLATES[i];
      const goal = pick(rng, t.goalChoices);
      const reward = Math.round(t.base + goal * t.rewardPerUnit);
      const id = `${t.type}:${slot}:${goal}`;
      return {
        id,
        type: t.type,
        goal,
        progress: 0,
        reward,
        done: false,
      };
    });
  }

  /** Bump progress for missions matching `type`. Returns coins earned by completions. */
  bump(type: MissionType, amount: number, opts?: { runScoped?: boolean }): number {
    const data = this.storage.get();
    let earned = 0;
    let changed = false;
    for (const m of data.missions.daily) {
      if (m.done) continue;
      const [mType] = m.id.split(":");
      if (mType !== type) continue;
      // run-scoped vs day-scoped is determined by the mission type itself;
      // the caller passes opts.runScoped only as a hint. We use type to decide.
      m.progress = Math.min(m.goal, m.progress + amount);
      changed = true;
      if (m.progress >= m.goal) {
        m.done = true;
        earned += m.reward;
      }
    }
    if (changed) this.storage.save();
    return earned;
  }

  /** Set absolute progress (used for "best in run" types). */
  setIfHigher(type: MissionType, value: number): number {
    const data = this.storage.get();
    let earned = 0;
    let changed = false;
    for (const m of data.missions.daily) {
      if (m.done) continue;
      const [mType] = m.id.split(":");
      if (mType !== type) continue;
      if (value > m.progress) {
        m.progress = Math.min(m.goal, value);
        changed = true;
        if (m.progress >= m.goal) {
          m.done = true;
          earned += m.reward;
        }
      }
    }
    if (changed) this.storage.save();
    return earned;
  }

  /** Reset run-scoped mission progress at the start of a new run. */
  resetRunScoped(): void {
    const runTypes: MissionType[] = ["coins_in_run", "distance", "lane_changes"];
    const data = this.storage.get();
    let changed = false;
    for (const m of data.missions.daily) {
      if (m.done) continue;
      const [mType] = m.id.split(":");
      if (runTypes.includes(mType as MissionType)) {
        if (m.progress !== 0) {
          m.progress = 0;
          changed = true;
        }
      }
    }
    if (changed) this.storage.save();
  }

  labelFor(m: ActiveMission): string {
    const tmpl = TEMPLATES.find((t) => t.type === m.type);
    return tmpl ? tmpl.label(m.goal) : m.id;
  }
}
